// Scene audio_script lines: "dialogue" is spoken by a character, "action" is what a
// character does on screen (never spoken), "narration" is the narrator voice-over.
export const SCRIPT_LINE_KINDS = ['dialogue', 'action', 'narration'];

export const SCRIPT_LINE_KIND_LABELS = {
  dialogue: 'Dialogue (spoken)',
  action: 'Action (not spoken)',
  narration: 'Narration',
};

export function scriptLineKind(kind) {
  const k = String(kind || 'dialogue').toLowerCase();
  return SCRIPT_LINE_KINDS.includes(k) ? k : 'dialogue';
}

// Who the dialogue camera shows during a line: '' = auto (the speaker), 'wide' = whole scene,
// or a character id (e.g. a reaction shot of the listener).
export const FOCUS_AUTO = '';
export const FOCUS_WIDE = 'wide';

export function scriptToDraft(script) {
  return (Array.isArray(script) ? script : [])
    .filter((seg) => seg && typeof seg === 'object')
    .map((seg) => ({
      kind: scriptLineKind(seg.kind),
      text: seg.text || '',
      character_id: seg.character_id || '',
      speaker: seg.speaker || '',
      action: seg.action || '',
      focus_character_id: seg.focus_character_id || FOCUS_AUTO,
    }));
}

export function scriptDraftToPayload(draft, characterById = {}) {
  return (draft || [])
    .filter((seg) => (seg?.text || '').trim())
    .map((seg) => {
      const text = (seg.text || '').trim();
      const kind = scriptLineKind(seg.kind);
      const focus = (seg.focus_character_id || '').trim();
      if (kind === 'narration') return focus ? { kind, text, focus_character_id: focus } : { kind, text };
      const cid = (seg.character_id || '').trim();
      const name = cid ? (characterById[cid] || seg.speaker || '') : (seg.speaker || '');
      const row = { kind, text, speaker: name || 'Character' };
      if (cid) row.character_id = cid;
      const acting = kind === 'dialogue' ? (seg.action || '').trim() : '';
      if (acting) row.action = acting;
      if (focus) row.focus_character_id = focus;
      return row;
    });
}

function _spokenKind(kind) {
  return String(kind || '').toLowerCase() !== 'action';
}

function _spokenText(value) {
  return String(value || '').trim();
}

/** Spoken dialogue/narration only — never actions, speaker labels, or scene overviews. */
export function spokenSubtitleEntries(scene) {
  const timeline = Array.isArray(scene?.audio_timeline) ? scene.audio_timeline : [];
  const fromTimeline = timeline
    .map((row, i) => {
      if (!row || typeof row !== 'object' || !_spokenKind(row.kind)) return null;
      const text = _spokenText(row.text);
      if (!text) return null;
      const start = Number(row.start_seconds);
      const dur = Number(row.duration_seconds);
      const end = Number(row.end_seconds);
      const durationSeconds = dur > 0 ? dur : (Number.isFinite(end) && Number.isFinite(start) ? Math.max(0, end - start) : 0);
      return {
        index: Number.isFinite(Number(row.index)) ? Number(row.index) : i,
        text,
        startSeconds: Number.isFinite(start) ? start : null,
        durationSeconds: durationSeconds > 0 ? durationSeconds : null,
      };
    })
    .filter(Boolean);
  if (fromTimeline.length) return fromTimeline;

  const script = Array.isArray(scene?.audio_script) ? scene.audio_script : [];
  return script
    .map((seg, i) => {
      if (!seg || typeof seg !== 'object' || !_spokenKind(seg.kind)) return null;
      const text = _spokenText(seg.text);
      if (!text) return null;
      return { index: i, text, startSeconds: null, durationSeconds: null };
    })
    .filter(Boolean);
}

/** Caption copy for a scene: words being spoken, not overview / scene_text / dialogue_text. */
export function spokenSubtitleTextForScene(scene) {
  const lines = spokenSubtitleEntries(scene).map((e) => e.text);
  if (lines.length) return lines.join('\n');
  const authored = _spokenText(scene?.authored_script);
  if (authored) return authored;
  const narr = _spokenText(scene?.narration_text) || _spokenText(scene?.text);
  if (narr) return narr;
  const script = scene?.audio_script;
  if (Array.isArray(script) && script.length) return '';
  return '';
}

export function sceneActionLines(scene, characterById = {}) {
  const script = Array.isArray(scene?.audio_script) ? scene.audio_script : [];
  const out = [];
  for (const seg of script) {
    if (!seg || typeof seg !== 'object') continue;
    const who = characterById[seg.character_id] || seg.speaker || 'Character';
    const kind = String(seg.kind || '').toLowerCase();
    if (kind === 'action' && String(seg.text || '').trim()) {
      out.push({ who, text: String(seg.text).trim(), whileSpeaking: false });
    } else if (kind === 'dialogue' && String(seg.action || '').trim()) {
      out.push({ who, text: String(seg.action).trim(), whileSpeaking: true });
    }
  }
  return out;
}

export const NARRATOR_SPEAKER_NAMES = new Set([
  'narrator',
  'narration',
  'voiceover',
  'voice over',
  'vo',
  'v.o',
  'v.o.',
  'रावी',
  'कथावाचक',
  'वाचक',
]);

const LABELED_SPEAKER_LINE_RE = /^(.{1,48}?)\s*[:：]\s+(\S.*)$/;
const GENERIC_INLINE_SPEAKER_RE = /(?:^|[\s।.?!,\u0964])([A-Za-z\u0900-\u097f][\w\u0900-\u097f .'\-]{0,39})\s*[:：]\s+/g;
const NOT_INLINE_SPEAKERS = new Set([
  'scene', 'int', 'ext', 'cut', 'fade', 'title', 'note', 'notes', 'slug',
  'action', 'http', 'https', 'www',
]);

export function isNarratorSpeaker(name) {
  return NARRATOR_SPEAKER_NAMES.has(String(name || '').trim().toLowerCase());
}

function _collectSpeakerMatches(blob, regex) {
  const matches = [];
  const pattern = new RegExp(regex.source, regex.flags);
  let match = pattern.exec(blob);
  while (match) {
    const speaker = String(match[1] || '').trim();
    if (speaker && !NOT_INLINE_SPEAKERS.has(speaker.toLowerCase())) {
      matches.push({
        speaker,
        index: match.index,
        end: match.index + match[0].length,
      });
    }
    match = pattern.exec(blob);
  }
  return matches;
}

export function splitSpeakerTurns(text, knownNames = []) {
  const blob = String(text || '').replace(/\r\n/g, '\n').trim();
  if (!blob) return [];
  const labels = [
    ...knownNames.map((name) => String(name || '').trim()).filter(Boolean),
    ...NARRATOR_SPEAKER_NAMES,
  ].filter(Boolean);
  let matches = [];
  if (labels.length) {
    const escaped = [...new Set(labels)]
      .sort((a, b) => b.length - a.length)
      .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    matches = _collectSpeakerMatches(
      blob,
      new RegExp(`(?:^|[\\s।.?!,\u0964])(${escaped.join('|')})\\s*[:：]\\s+`, 'gi'),
    );
  }
  const generic = _collectSpeakerMatches(blob, GENERIC_INLINE_SPEAKER_RE);
  if (generic.length > matches.length) matches = generic;
  if (!matches.length) return [];
  const turns = [];
  if (matches[0].index > 0) {
    const lead = blob.slice(0, matches[0].index).trim();
    if (lead) turns.push({ speaker: '', text: lead });
  }
  for (let i = 0; i < matches.length; i += 1) {
    const end = i + 1 < matches.length ? matches[i + 1].index : blob.length;
    const spoken = blob.slice(matches[i].end, end).trim();
    if (!matches[i].speaker || !spoken) continue;
    turns.push({ speaker: matches[i].speaker, text: spoken });
  }
  return turns;
}

/** Spoken dialogue/narration rows from a stored scene script (actions omitted). */
export function spokenAudioScriptLines(scene) {
  const script = Array.isArray(scene?.audio_script) ? scene.audio_script : [];
  return script.filter((seg) => (
    seg
    && typeof seg === 'object'
    && String(seg.kind || '').toLowerCase() !== 'action'
    && String(seg.text || '').trim()
  ));
}

/** Parse ``NAME: line`` copy into typed audio_script rows. Empty when there are no labels. */
export function parseLabeledScriptToAudioScript(text, characters = []) {
  const nameToId = {};
  const knownNames = [];
  for (const character of characters) {
    const name = String(character?.name || '').trim();
    if (name && character?.id) {
      nameToId[name.toLowerCase()] = character.id;
      knownNames.push(name);
    }
  }
  const turns = splitSpeakerTurns(text, knownNames);
  const rowsFromTurns = (turnsList) => {
    const out = [];
    let labeled = 0;
    for (const turn of turnsList) {
      const speaker = String(turn.speaker || '').trim();
      const spoken = String(turn.text || '').trim();
      if (!spoken) continue;
      if (!speaker || isNarratorSpeaker(speaker)) {
        out.push(speaker ? { kind: 'narration', text: spoken, speaker } : { kind: 'narration', text: spoken });
        continue;
      }
      labeled += 1;
      const row = { kind: 'dialogue', text: spoken, speaker };
      const characterId = nameToId[speaker.toLowerCase()];
      if (characterId) row.character_id = characterId;
      out.push(row);
    }
    return labeled ? out : [];
  };
  const fromTurns = rowsFromTurns(turns);
  if (fromTurns.length) return fromTurns;

  const out = [];
  let labeled = 0;
  for (const raw of String(text || '').replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const match = line.match(LABELED_SPEAKER_LINE_RE);
    if (!match) {
      out.push({ kind: 'narration', text: line });
      continue;
    }
    const speaker = String(match[1] || '').replace(/\s+/g, ' ').trim();
    const spoken = String(match[2] || '').trim();
    if (!speaker || !spoken) continue;
    labeled += 1;
    if (isNarratorSpeaker(speaker)) {
      out.push({ kind: 'narration', text: spoken, speaker });
      continue;
    }
    const row = { kind: 'dialogue', text: spoken, speaker };
    const characterId = nameToId[speaker.toLowerCase()];
    if (characterId) row.character_id = characterId;
    out.push(row);
  }
  return labeled ? out : [];
}

function _escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function _dialogueSpeakerCount(script) {
  const keys = new Set();
  for (const seg of script || []) {
    if (String(seg?.kind || '').toLowerCase() !== 'dialogue') continue;
    const key = String(seg.character_id || seg.speaker || '').trim();
    if (key) keys.add(key);
  }
  return keys.size;
}

export function expandAudioScriptSpeakers(script, characters = []) {
  const out = [];
  for (const seg of script || []) {
    if (!seg || typeof seg !== 'object') continue;
    if (String(seg.kind || '').toLowerCase() === 'action') {
      out.push({ ...seg });
      continue;
    }
    const text = String(seg.text || '').trim();
    if (!text) continue;
    const speaker = String(seg.speaker || '').trim();
    const speakerPrefix = speaker ? new RegExp(`^${_escapeRegExp(speaker)}\\s*[:：]\\s+`, 'i') : null;
    const blob = speaker && speakerPrefix && !speakerPrefix.test(text)
      ? `${speaker}: ${text}`
      : text;
    let parsed = parseLabeledScriptToAudioScript(blob, characters);
    if (parsed.length < 2) parsed = parseLabeledScriptToAudioScript(text, characters);
    if (parsed.length >= 2) {
      out.push(...parsed);
      continue;
    }
    const cleaned = speakerPrefix ? text.replace(speakerPrefix, '').trim() : text;
    out.push({ ...seg, text: cleaned || text });
  }
  return out;
}

/**
 * Audio generation should dub each character with their voice and use narrator TTS
 * for narration. Packed ``NAME: line`` rows are split so speaker tags are never spoken.
 */
export function resolveSceneAudioScriptForTts({ scene, scriptText = '', characters = [] } = {}) {
  const typed = expandAudioScriptSpeakers(spokenAudioScriptLines(scene), characters);
  const parsed = parseLabeledScriptToAudioScript(scriptText, characters);
  const typedCount = _dialogueSpeakerCount(typed);
  const parsedCount = _dialogueSpeakerCount(parsed);
  if (parsedCount > typedCount) return parsed;
  if (typedCount >= 1) return typed;
  if (parsed.length) return parsed;
  if (typed.length) return typed;
  const fallback = String(scriptText || '').trim();
  return fallback ? [{ kind: 'narration', text: fallback }] : [];
}

export function characterVoicesPayload(characters = []) {
  const voices = {};
  for (const character of characters) {
    if (!character?.id) continue;
    const row = {};
    const minimax = String(character.minimax_voice_id || '').trim();
    const eleven = String(character.elevenlabs_voice_id || '').trim();
    const murf = String(character.murf_voice_id || '').trim();
    const murfLocale = String(character.murf_locale || '').trim();
    const dialogueProvider = String(character.dialogue_tts_provider || '').trim();
    const runwayVoice = String(character.runway_voice_id || '').trim();
    const runwayModel = String(character.runway_model || '').trim();
    const edge = String(character.edge_voice || '').trim();
    if (minimax) row.minimax_voice_id = minimax;
    if (eleven) row.elevenlabs_voice_id = eleven;
    if (murf) row.murf_voice_id = murf;
    if (murfLocale) row.murf_locale = murfLocale;
    if (dialogueProvider && dialogueProvider !== 'auto') row.dialogue_tts_provider = dialogueProvider;
    if (runwayVoice) row.runway_voice_id = runwayVoice;
    if (runwayModel) row.runway_model = runwayModel;
    if (edge) row.edge_voice = edge;
    if (Object.keys(row).length) voices[character.id] = row;
  }
  return voices;
}
